import { Component, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { WorkflowActionDialogComponent } from "../../components/workflow-action-dialog/workflow-action-dialog.component";
import { WorkflowLogsDialogComponent } from "../../components/workflow-logs-dialog/workflow-logs-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { SessionService } from "src/app/services/session.service";
import { NavigationService } from "src/app/services/navigation.service";
import { DashboardService } from "src/app/services/dashboard.service";
import { DialogService } from "src/app/services/dialog.service";
import { DeleteTemplateComponent } from "../../../shared/secure-mail/delete-template/delete-template.component";
import { WorkflowDefinition, WorkflowService } from "../../service/workflow.service";
import { TaskMgmtService } from "../../service/task-mgmt.service";
import { ActivatedRoute } from "@angular/router";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-workflow-management",
  templateUrl: "./workflow-management.component.html",
  styleUrls: ["./workflow-management.component.scss"],
})
export class WorkflowManagementComponent implements OnInit, OnDestroy {
  @Input("moduleName") moduleName: any;
  @Input("moduleKey") moduleKey: any;
  @Input("clientKey") clientKey: any;

  private reqSubcription: Subscription[] = [];
  private suppressNextRowClick = false;

  gridApi!: GridApi;
  columnDefs: ColDef[] = [];
  rowHeight: number = 52;
  rowStyle: any;
  rowData: WorkflowDefinition[] = [];
  filteredRowData: WorkflowDefinition[] = [];
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 120,
    resizable: true,
    sortable: false,
    suppressMenu: true,
  };
  rowSelection: "single" = "single";
  paginationPageSize: number = 10;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  currentPage: number = 0;
  totalPages: number = 1;
  visiblePages: (number | string)[] = [1];

  searchText: string = "";
  selectedFilter: string = "";
  isLoadingDefs: boolean = false;

  // Brand selector — drives the email-template lookup in the workflow
  // designer (passed through navigation). Auto-selects the first brand on
  // load to mirror task-management-wf so the page is never in a "no brand
  // chosen" state when the user clicks Create.
  brands: any[] = [];
  filteredBrands: any[] = [];
  selectedBrand: any = null;
  brandDropdownOpen: boolean = false;
  brandSearchText: string = '';
  isLoadingBrands: boolean = false;
  dashboardKey: string = '';

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.onPaginationChanged();
  }

  /** Called when AG-Grid pagination state changes */
  onPaginationChanged(): void {
    if (!this.gridApi) return;
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.totalPages = this.gridApi.paginationGetTotalPages();
    this.updateVisiblePages();
  }

  /** Change page size from the custom dropdown */
  onPageSizeChange(newSize: number): void {
    this.paginationPageSize = newSize;
    if (this.gridApi) {
      this.gridApi.paginationSetPageSize(newSize);
    }
  }

  /** Navigate to a specific page */
  goToPage(page: number): void {
    if (this.gridApi && page >= 0 && page < this.totalPages) {
      this.gridApi.paginationGoToPage(page);
    }
  }

  /** Build the visible page numbers array with ellipsis */
  private updateVisiblePages(): void {
    const pages: (number | string)[] = [];
    const total = this.totalPages;
    const current = this.currentPage + 1; // 1-indexed for display

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push('...');

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (current < total - 2) pages.push('...');
      pages.push(total);
    }
    this.visiblePages = pages;
  }

  constructor(
    public dialog: MatDialog,
    private sessionService: SessionService,
    private navigationService: NavigationService,
    private dashboardService: DashboardService,
    private workflowService: WorkflowService,
    private dialogService: DialogService,
    private taskMgmtService: TaskMgmtService,
    private activeRoute: ActivatedRoute
  ) {
    this.rowStyle = {
      padding: "0px 0px",
    };
  }

  ngOnInit(): void {
    this.activeRoute.params.subscribe((p) => {
      // this.moduleName = p["module"] || "";

      this.activeRoute.queryParams.subscribe((params: any) => {
        let decryptedParams = this.navigationService.decryptData(params);

        // this.clientKey = decryptedParams["cl_key"];
        // this.moduleKey = decryptedParams["md_key"];
        this.dashboardKey = decryptedParams["ds_key"];
      });
    });

    this.dashboardService.moduleKey = this.moduleKey;
    this.loadDataNewGridConfig();
    this.loadDefinitions();
    this.loadBrands();
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((s) => s.unsubscribe());
  }

  private loadDefinitions(): void {
    this.isLoadingDefs = true;
    // finalize() guarantees the loader is cleared on any terminal signal
    // (next, error, or unsubscribe) — including if the service's
    // catchError contract changes in the future.
    const sub = this.workflowService.getDefinitions()
      .pipe(finalize(() => { this.isLoadingDefs = false; }))
      .subscribe({
        next: (defs) => {
          this.rowData = defs || [];
          this.applyFilters();
        },
      });
    this.reqSubcription.push(sub);
  }

  /** Quick-search across all visible columns */
  onSearchChange(): void {
    this.applyFilters();
  }

  /** Filter by workflow type dropdown */
  onFilterChange(): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    let data = [...this.rowData];

    if (this.selectedFilter) {
      data = data.filter((row) => row.trigger_type === this.selectedFilter);
    }

    if (this.searchText && this.searchText.trim()) {
      const term = this.searchText.trim().toLowerCase();
      data = data.filter(
        (row) =>
          (row.wf_name && row.wf_name.toLowerCase().includes(term)) ||
          (row.description && row.description.toLowerCase().includes(term)) ||
          (row.trigger_type && row.trigger_type.toLowerCase().includes(term)) ||
          (row.status && row.status.toLowerCase().includes(term))
      );
    }

    this.filteredRowData = data;
  }

  addWorkFlow(workflowType: string = 'trigger'): void {
    const dialogRef = this.dialog.open(WorkflowActionDialogComponent, {
      width: "80vw",
      maxWidth: "600px",
      maxHeight: "85vh",
      autoFocus: false,
      data: { type: workflowType },
    });
    const sub = dialogRef.afterClosed().subscribe((result) => {
      if (!result?.definition) return;

      // Refresh the list immediately so the newly-created workflow is visible
      // even if the navigation that follows fails for any reason (encrypt
      // throw, route guard, etc.). Without this, a failed navigation leaves
      // the user staring at a stale list with no sign their workflow was
      // created.
      this.loadDefinitions();

      const def: WorkflowDefinition = result.definition;
      const rawTrigger: 'schedule' | 'event' | 'manual' =
        (def.trigger_type as any) ||
        (result.triggerConfig?.frequency ? 'schedule' :
         result.triggerConfig?.event_type_key ? 'event' : 'manual');

      const triggerStepTypeId =
        rawTrigger === 'schedule' ? 'trigger_schedule' :
        rawTrigger === 'event'    ? 'trigger_event'    :
                                    'trigger_manual';

      const stepLabel =
        rawTrigger === 'schedule' ? 'Schedule' :
        rawTrigger === 'event'    ? 'Event Trigger' :
                                    'Manual Trigger';

      const initialDesign = {
        version: 1,
        canvas: { scale: 1, pan_x: 60, pan_y: 60 },
        steps: [{
          step_key: 'step_1',
          step_type_id: triggerStepTypeId,
          step_label: stepLabel,
          canvas_x: 0,
          canvas_y: 0,
          step_order: 1,
          config: result.triggerConfig || {},
        }],
        connections: [],
      };

      const designerData = {
        selectedworkflow:
          rawTrigger === 'schedule' ? 'Schedule Workflow' : 'Trigger Workflow',
        wf_def_id: def.wf_def_id,
        workflowName: def.wf_name,
        workflowTrigger: rawTrigger,
        triggerConfig: result.triggerConfig,
      };

      const query: any = {
        tab_id: 0,
        cl_key: this.clientKey,
        md_key: this.moduleKey,
        ds_key: this.dashboardKey,
        brand_key: this.selectedBrand?.brand_key ?? null,
        // toAsciiJson escapes non-Latin1 chars (em-dash in event_type, etc.)
        // to \uXXXX so the encrypted query param never feeds non-Latin1
        // bytes to btoa(). JSON.parse on the receiving side handles the
        // escapes natively.
        designerData: this.toAsciiJson(designerData),
      };

      // Write the initial design and THEN navigate. We wait for the response
      // (success or failure) for two reasons:
      //   1. Navigating immediately destroys this component and unsubscribes
      //      saveFull mid-flight, leaving the workflow without a design.
      //   2. The backend needs to commit before the designer's loadFull fires,
      //      otherwise the designer enters its `loadFailed` state and shows
      //      "Could not load this workflow."
      // Navigation is NOT gated on save success — even if the design write
      // fails, we still send the user to the designer (the workflow row
      // already exists from createDefinition).
      const navigate = () => {
        this.navigationService.routerNavigate(
          `dashboard/${this.moduleName}/${"direct"}/${"Designer"}`,
          query
        );
      };
      const saveSub = this.workflowService.saveFull(def.wf_def_id, {
        wf_name: def.wf_name,
        description: (def as any).description || '',
        updated_by: this.sessionService.getUsername() || 'system',
        design: initialDesign,
      }).subscribe({
        next: (saveRes: any) => {
          if (!saveRes) {
            this.dialogService.SnackBar(
              'Initial workflow design could not be saved. Please save it from the designer.',
              'Close'
            );
          }
          navigate();
        },
        error: () => navigate(),
      });
      this.reqSubcription.push(saveSub);
    });
    this.reqSubcription.push(sub);
  }

  onRowClicked(event: any): void {
    if (this.suppressNextRowClick) {
      this.suppressNextRowClick = false;
      return;
    }
    const def: WorkflowDefinition = event.data;
    const designerData = {
      selectedworkflow:
        def.trigger_type === 'schedule' ? 'Schedule Workflow' : 'Trigger Workflow',
      wf_def_id: def.wf_def_id,
      workflowName: def.wf_name,
      workflowTrigger: def.trigger_type,
    };

    const query: any = {
      tab_id: 0,
      cl_key: this.clientKey,
      md_key: this.moduleKey,
      ds_key: this.dashboardKey,
      brand_key: this.selectedBrand?.brand_key ?? null,
      designerData: this.toAsciiJson(designerData),
    };
    this.navigationService.routerNavigate(
      `dashboard/${this.moduleName}/${"direct"}/${"Designer"}`,
      query
    );
  }

  // ASCII-safe JSON: every non-Latin1 char (em-dash —, smart quotes, emoji,
  // accented brand/workflow names, etc.) becomes a \uXXXX escape so the
  // resulting string contains only ASCII bytes. This avoids the
  // InvalidCharacterError thrown by btoa() inside CryptoService when the
  // encrypted query param contains Unicode beyond Latin1. The receiving
  // designer's JSON.parse natively converts \uXXXX back to the original
  // character — no changes needed on the decode side.
  private toAsciiJson(obj: any): string {
    const json = JSON.stringify(obj);
    let out = '';
    for (let i = 0; i < json.length; i++) {
      const code = json.charCodeAt(i);
      out += code < 0x80 ? json[i] : '\\u' + ('0000' + code.toString(16)).slice(-4);
    }
    return out;
  }

  // Dead helper — superseded by toAsciiJson above. Left in place because
  // its body contains a Unicode character class that the file editor cannot
  // round-trip cleanly. Safe to delete in any future cleanup PR.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _toAsciiJson_v1_dead(obj: any): string {
    return JSON.stringify(obj).replace(
      /[-￿]/g,
      (c) => '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4)
    );
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hh = String(hours).padStart(2, "0");
    return `${year}-${month}-${day} ${hh}:${minutes} ${ampm}`;
  }

  loadDataNewGridConfig(): void {
    const triggerColors: Record<string, { bdr: string; bg: string }> = {
      schedule: { bdr: "#1363DF", bg: "rgba(19,99,223,0.08)" },
      event: { bdr: "#E8853D", bg: "rgba(232,133,61,0.08)" },
      manual: { bdr: "#737D88", bg: "rgba(115,125,136,0.08)" },
    };
    const statusColors: Record<string, { bdr: string; bg: string }> = {
      DRAFT: { bdr: "#737D88", bg: "rgba(115,125,136,0.08)" },
      ACTIVE: { bdr: "#49AF41", bg: "rgba(73,175,65,0.1)" },
      PAUSED: { bdr: "#E8853D", bg: "rgba(232,133,61,0.08)" },
      ARCHIVED: { bdr: "#a9a9a9", bg: "rgba(169,169,169,0.1)" },
    };
    const pill = (text: string, c: { bdr: string; bg: string }): string => `<span style="
      display: inline-block;
      padding: 4px 14px;
      border: 1.5px solid ${c.bdr};
      border-radius: 20px;
      color: ${c.bdr};
      background: ${c.bg};
      font-size: 12px;
      font-weight: 500;
      font-family: 'Poppins', sans-serif;
      line-height: 1.4;
      white-space: nowrap;
    ">${text}</span>`;

    this.columnDefs = [
      {
        headerName: "Workflow Name",
        field: "wf_name",
        minWidth: 200,
        tooltipField: "wf_name",
        cellStyle: {
          "font-weight": "500",
          color: "#333",
        },
        cellRenderer: (params: any) => {
          const div = document.createElement("div");
          div.className = "wf-name-cell";
          div.textContent = params.value ?? "";
          return div;
        },
      },
      {
        headerName: "Trigger Type",
        field: "trigger_type",
        cellStyle: { "text-align": "center" },
        cellRenderer: (params: any) => {
          if (!params.value) return "";
          const key = String(params.value).toLowerCase();
          const c = triggerColors[key] ?? triggerColors["manual"];
          const label = key.charAt(0).toUpperCase() + key.slice(1);
          return pill(label, c);
        },
      },
      {
        headerName: "Status",
        field: "status",
        cellStyle: { "text-align": "center" },
        cellRenderer: (params: any) => {
          if (!params.value) return "";
          const key = String(params.value).toUpperCase();
          const c = statusColors[key] ?? statusColors["DRAFT"];
          return pill(key, c);
        },
      },
      {
        headerName: "Created By",
        field: "created_by",
        cellStyle: { "text-align": "center", color: "#555" },
      },
      {
        headerName: "Updated",
        field: "updated_time",
        cellStyle: { "text-align": "center", color: "#555" },
        valueFormatter: (params: any) => {
          return this.formatDate(params.value);
        },
      },
      {
        headerName: "Activation Date",
        field: "activated_time",
        cellStyle: { "text-align": "center", color: "#555" },
        valueFormatter: (params: any) => {
          return params.value ? this.formatDate(params.value) : "—";
        },
      },
      {
        headerName: "",
        field: "actions",
        maxWidth: 60,
        minWidth: 60,
        sortable: false,
        suppressMenu: true,
        cellStyle: {
          "justify-content": "center",
          "text-align": "center",
          cursor: "pointer",
        },
        cellRenderer: () => {
          return `<span class="workflow-eye-icon" title="View">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          </span>`;
        },
      },
      {
        headerName: "",
        field: "wf_logs_action",
        maxWidth: 140,
        minWidth: 140,
        sortable: false,
        suppressMenu: true,
        cellStyle: {
          "justify-content": "center",
          "text-align": "center",
          cursor: "pointer",
        },
        cellRenderer: () => {
          return `<button title="View Logs" style="
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 5px 12px;
            background: rgba(19,99,223,0.07);
            border: 1.5px solid rgba(19,99,223,0.3);
            border-radius: 20px;
            color: #1363DF;
            font-size: 12px;
            font-weight: 500;
            font-family: 'Poppins', sans-serif;
            cursor: pointer;
            white-space: nowrap;
            transition: background 0.15s;
          ">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1363DF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            View Logs
          </button>`;
        },
      },
      {
        headerName: "",
        field: "wf_delete_action",
        maxWidth: 60,
        minWidth: 60,
        sortable: false,
        suppressMenu: true,
        cellStyle: {
          "justify-content": "center",
          "text-align": "center",
          cursor: "pointer",
        },
        cellRenderer: (params: any) => {
          // if (params.data?.status !== 'DRAFT') return '';
          return `<span title="Delete" style="display:inline-flex;align-items:center;justify-content:center;cursor:pointer;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e53e3e" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14H6L5 6"></path>
              <path d="M10 11v6"></path>
              <path d="M14 11v6"></path>
              <path d="M9 6V4h6v2"></path>
            </svg>
          </span>`;
        },
      },
    ];

    this.filteredRowData = [...this.rowData];

    if (this.gridApi) {
      this.gridApi.setRowData(this.filteredRowData);
    }
  }

  deleteWorkflow(wfDefId: number, rowData: any, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DeleteTemplateComponent, {
      width: '40%',
      height: '35%',
      disableClose: false,
      panelClass: 'du-custom-modalbox',
      autoFocus: false,
      data: {
        entityName: 'workflow',
        entityTitle: 'Workflow',
        workflow: rowData,
      },
    });

    const closeSub = dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== 'delete') return;
      const sub = this.workflowService.deleteDefinition(wfDefId).subscribe((res: any) => {
        if (!res) return; // handleError already surfaced the failure
        this.dialogService.SnackBar('Workflow deleted', 'Close');
        this.loadDefinitions();
      });
      this.reqSubcription.push(sub);
    });
    this.reqSubcription.push(closeSub);
  }

  onGridCellClicked(params: any): void {
    if (params.colDef?.field === 'wf_logs_action') {
      this.suppressNextRowClick = true;
      this.openLogsDialog(params.data);
    // } else if (params.colDef?.field === 'wf_delete_action' && params.data?.status === 'DRAFT') {
    } else if (params.colDef?.field === 'wf_delete_action') {
      this.suppressNextRowClick = true;
      this.deleteWorkflow(params.data.wf_def_id, params.data, params.event);
    }
  }

  openLogsDialog(workflow: WorkflowDefinition): void {
    this.dialog.open(WorkflowLogsDialogComponent, {
      width: '80vw',
      maxWidth: '860px',
      maxHeight: '90vh',
      autoFocus: false,
      data: { workflowName: workflow.wf_name },
    });
  }

  // ==========================================================
  //  Brand dropdown — mirrors task-management-wf so the user can
  //  pick a brand at the workflow level. The selection is forwarded
  //  to the designer via query params and used as the brand_key for
  //  the email-template lookup inside Email Alert steps.
  // ==========================================================

  private loadBrands(): void {
    this.isLoadingBrands = true;
    const sub = this.taskMgmtService.loadBrands().subscribe({
      next: (response: any) => {
        const allitems = Array.isArray(response) ? response : [];
        const items = environment.typeOfEnv === 'DEMO' ? allitems.filter(x=>x.brand_name === "Jupiterol") : allitems;
        this.brands = items;
        this.filteredBrands = [...items];
        this.isLoadingBrands = false;
        if (!this.selectedBrand && items.length > 0) {
         environment.typeOfEnv === 'DEMO' ? this.selectedBrand = items[0] : 
          this.selectedBrand = items.find((f: any) => f.brand_key == '200104');
        }
      },
      error: () => {
        this.brands = [];
        this.filteredBrands = [];
        this.isLoadingBrands = false;
      },
    });
    this.reqSubcription.push(sub);
  }

  toggleBrandDropdown(event: Event): void {
    event.stopPropagation();
    this.brandDropdownOpen = !this.brandDropdownOpen;
    if (this.brandDropdownOpen) {
      this.brandSearchText = '';
      this.filteredBrands = [...this.brands];
    }
  }

  onBrandSearch(searchText: string): void {
    this.brandSearchText = searchText;
    if (!searchText.trim()) {
      this.filteredBrands = [...this.brands];
      return;
    }
    const lower = searchText.toLowerCase().trim();
    this.filteredBrands = this.brands.filter(
      (b) => (b.brand_name || '').toLowerCase().includes(lower)
    );
  }

  selectBrand(brand: any): void {
    this.selectedBrand = brand;
    this.brandDropdownOpen = false;
    this.brandSearchText = '';
    this.filteredBrands = [...this.brands];
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.brandDropdownOpen = false;
  }

}
